import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, ArrowLeft, RefreshCw, Mail, Phone } from 'lucide-react';
import { Button, Card } from '../../components/ui';
import { useRegistrationStore } from '../../stores/registrationStore';

const PaymentCanceledPage: React.FC = () => {
  const { registrationData } = useRegistrationStore();

  const getTierName = () => {
    switch (registrationData?.selectedTierId) {
      case 1:
        return 'Starter';
      case 2:
        return 'Growth';
      case 3:
        return 'Professional';
      default:
        return 'Selected';
    }
  };

  const handleTryAgain = () => {
    window.location.href = '/onboarding/checkout';
  };

  const handleChangePlan = () => {
    window.location.href = '/pricing';
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@athletiverse.com?subject=Payment%20Issue';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl mx-auto"
        >
          <Card className="p-5 sm:p-6">
            {/* Error Icon */}
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center"
              >
                <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
              </motion.div>
            </div>

            {/* Title */}
            <div className="text-center mb-5">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                Payment Setup Canceled
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Your trial setup for the {getTierName()} plan wasn't completed
              </p>
            </div>

            {/* Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
              <p className="text-sm text-gray-700 text-center">
                Don't worry! Your account information has been saved. You can return to
                payment setup anytime or choose a different plan.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 mb-5">
              <Button
                variant="primary"
                size="md"
                className="w-full text-sm"
                onClick={handleTryAgain}
              >
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Try Payment Setup Again
              </Button>

              <Button
                variant="outline"
                size="md"
                className="w-full text-sm"
                onClick={handleChangePlan}
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Choose a Different Plan
              </Button>
            </div>

            {/* Help Section */}
            <div className="border-t border-gray-200 pt-5">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 text-center">
                Need Help?
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={handleContactSupport}
                  className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <Mail className="w-5 h-5 text-avi-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Support</p>
                    <p className="text-xs text-gray-600">support@athletiverse.com</p>
                  </div>
                </button>

                <a
                  href="/support"
                  className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Phone className="w-5 h-5 text-avi-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Support Center</p>
                    <p className="text-xs text-gray-600">Visit our help center</p>
                  </div>
                </a>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800 text-center">
                  <strong>Common issues:</strong> If you're having trouble with payment setup,
                  check that your card details are correct and that your card supports online
                  transactions. Contact your bank if the issue persists.
                </p>
              </div>
            </div>

            {/* Alternative Options */}
            <div className="mt-5 pt-4 border-t border-gray-200">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 text-center">
                You can also:
              </h3>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    Try a different payment method or card
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    Choose a different subscription plan that better fits your needs
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    Contact us to discuss alternative payment arrangements
                  </span>
                </li>
              </ul>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentCanceledPage;
